import { Fragment, useMemo, useState } from 'react'
import {
  DRIVE_MASS_TARGET,
  EMPTY_FILTER,
  applyBomFilter,
  bomGroups,
  filterActive,
  filterSummary,
  MOTOR_BY_ID,
  SET_MOTOR_MASS,
  computeBom,
  type BomEvidence,
  type BomFilter,
  type BomResult,
  type MassSource,
} from '@/domain/hs1'
import { bomEvidence } from '@/domain/plm'
import { Alert, Badge, Button, Card, Table, Td, Th } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, PageHead, num } from '@/sim/parts'

/**
 * Stückliste — die Brücke zwischen Rechnung und Konstruktion.
 *
 * DIE SEITE SCHLIESST DEN KREIS. Die Antriebsliste sagt, welches Moment jede
 * Achse braucht; hier wird daraus eine Masse, und die Masse muss wieder
 * getragen werden. Am Ende steht eine Gesamtmasse, die man gegen die
 * eingestellte Körpermasse halten kann — genau der Abgleich, den die
 * Modellseite verlangt.
 *
 * JEDE EINZELMASSE IST ÜBERSCHREIBBAR, und der Schätzwert bleibt daneben
 * stehen. Sobald eine Baugruppe gewogen ist, trägt man sie ein; die Summe
 * rechnet mit dem echten Wert weiter, und die überschriebenen Zeilen bleiben
 * auch dann stehen, wenn sich Gangart oder Regler ändern.
 */

const fmt = (value: number, digits = 2): string =>
  Number.isFinite(value) ? value.toFixed(digits).replace('.', ',') : '∞'

/**
 * Kosten in Euro, ohne Nachkommastellen und mit Tausenderpunkt.
 *
 * Bewusst gerundet: eine Zahl mit Cent-Stellen sähe aus wie ein Angebot, und
 * das ist sie nicht. Grössenordnungen brauchen keine Genauigkeit, die sie nicht
 * haben.
 */
const eur = (value: number): string =>
  Number.isFinite(value) ? Math.round(value).toLocaleString('de-DE') : '—'

/** Die Kostengruppen und woraus sie sich ergeben. */
const COST_ROWS: { key: keyof BomResult['cost']; label: string; basis: string }[] = [
  { key: 'actuators', label: 'Antriebe', basis: 'Antriebsmasse × Satz je Kilogramm' },
  { key: 'battery', label: 'Akku', basis: 'geforderte Energie × Satz je Wattstunde' },
  { key: 'capacitor', label: 'Zwischenkreiskondensator', basis: 'Kapazität × Satz je Farad' },
  { key: 'electronics', label: 'Elektronik', basis: 'Stückzahl × Satz je Position' },
  { key: 'sensors', label: 'Sensorik', basis: 'Stückzahl × Satz je Position' },
  { key: 'misc', label: 'Hände, Füsse', basis: 'Stückzahl × Satz je Position' },
  { key: 'structure', label: 'Struktur und Fertigung', basis: 'Strukturmasse × Satz je Kilogramm' },
]

/** Eine überschreibbare Masse. */
function MassCell({
  value,
  estimate,
  overridden,
  onChange,
}: {
  value: number
  estimate: number
  overridden: boolean
  onChange: (value: number | null) => void
}) {
  const [draft, setDraft] = useState<string | null>(null)

  return (
    <span className="bom__mass">
      <input
        className={`bom__input${overridden ? ' bom__input--set' : ''}`}
        type="number"
        step="0.01"
        min="0"
        inputMode="decimal"
        value={draft ?? value.toFixed(2)}
        title={`Schätzwert ${fmt(estimate)} kg — überschreibbar`}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          if (draft === null) return
          const parsed = Number(draft.replace(',', '.'))
          onChange(draft.trim() === '' || !Number.isFinite(parsed) || parsed < 0 ? null : parsed)
          setDraft(null)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur()
          if (event.key === 'Escape') {
            setDraft(null)
            event.currentTarget.blur()
          }
        }}
      />
      <span className="bom__unit">kg</span>
    </span>
  )
}

/**
 * Woher die Masse einer Zeile kommt — als Marke, nicht als Wort im Fliesstext.
 *
 * VIER STUFEN, VIER TÖNE, und die Reihenfolge ist die der Verbindlichkeit.
 * „Gemessen" ist die einzige, die nicht in diesem Repository entstanden ist;
 * sie bekommt deshalb den ruhigen Ton, nicht den lauten — sie ist der
 * Normalfall, den man anstrebt, kein Zustand, den man beheben muss.
 *
 * „Geschätzt" bekommt den warnenden. Nicht weil eine Schätzung falsch wäre,
 * sondern weil eine Stückliste, die überwiegend aus Schätzungen besteht, keine
 * Freigabe trägt — und das soll man sehen, ohne die Spalte zu lesen.
 */
const HERKUNFT: Record<MassSource, { label: string; tone: 'success' | 'accent' | 'neutral' | 'warning' }> = {
  plm: { label: 'gemessen', tone: 'success' },
  ueberschrieben: { label: 'von Hand', tone: 'accent' },
  gesetzt: { label: 'ausgewählt', tone: 'neutral' },
  geschaetzt: { label: 'geschätzt', tone: 'warning' },
}

/** Von verbindlich nach vermutet — die Reihenfolge ist die Aussage. */
const HERKUNFT_ORDER: MassSource[] = ['plm', 'ueberschrieben', 'gesetzt', 'geschaetzt']

/** Nur die Bezeichnungen — für `filterSummary`, das keine Töne kennt. */
const HERKUNFT_TEXT: Record<MassSource, string> = {
  plm: HERKUNFT.plm.label,
  ueberschrieben: HERKUNFT.ueberschrieben.label,
  gesetzt: HERKUNFT.gesetzt.label,
  geschaetzt: HERKUNFT.geschaetzt.label,
}

const HERKUNFT_NOTE: Record<MassSource, string> = {
  plm: 'am Bauteil gewogen, aus dem führenden System',
  ueberschrieben: 'von Hand eingetragen — schlägt alles darunter',
  gesetzt: 'aus der gesetzten Motorisierung des Gen5-Stands',
  geschaetzt: 'Moment durch Momentendichte, oder Erfahrungswert',
}

function Herkunft({ row }: { row: { massSource: MassSource; evidence: BomEvidence | null } }) {
  const h = HERKUNFT[row.massSource]
  return (
    <>
      <Badge tone={h.tone}>{h.label}</Badge>
      {row.evidence && (
        <div className="cell-muted">
          {row.evidence.number} · Rev. {row.evidence.revision} · {row.evidence.state}
        </div>
      )}
    </>
  )
}

export function PartsListPage() {
  /*
    DIE ANNAHMEN LIEGEN JETZT IM PROVIDER und werden unter Parameter verstellt.
    Hier wird nur noch mit ihnen gerechnet — und das ist die richtige
    Arbeitsteilung: diese Seite ZEIGT, was aus den Eingaben folgt, sie ist
    nicht selbst ein Eingabeort.

    Die Handeingaben ziehen mit, weil sie sonst beim Seitenwechsel verloren
    gingen.
  */
  const {
    model,
    state,
    simulation,
    bom: params,
    bomOverrides: overrides,
    setBomOverride,
    resetBomOverrides,
    plm,
  } = useSimulation()

  /*
    DIE NACHWEISE AUS DEM PLM.

    Heute leer, und zwar nicht, weil das PLM nicht antwortet, sondern weil es
    Teile SEGMENTEN zuordnet und nicht Stuecklistenpositionen. Das Feld dafuer
    heisst `PlmPart.bomKey` und wird von der Gegenstelle noch nicht gefuellt.

    Der Weg steht trotzdem: sobald etwas kommt, tragen die Zeilen es, ohne dass
    hier eine Zeile zu aendern waere.
  */
  const evidence = useMemo(() => bomEvidence(plm), [plm])

  const demands = useMemo(() => simulation.drives.map((d) => d.demand), [simulation.drives])

  const result = useMemo(
    () =>
      computeBom(
        model,
        demands,
        state,
        params,
        simulation.gait.cycleTime,
        /* Die gerechnete Akkuleistung, nicht die rohe mechanische Summe: die
           wird über den Gehzyklus negativ, weil die inverse Dynamik verlustfrei
           ist. Was hier ankommt, hat Feder, Zwischenkreis und Halteverluste
           schon gesehen — die drei Schalter unter Merkmale bestimmen es. */
        simulation.power.net,
        overrides,
        evidence,
      ),
    [
      model,
      demands,
      state,
      params,
      simulation.gait.cycleTime,
      simulation.power.net,
      overrides,
      evidence,
    ],
  )

  const setOverride = (key: string) => (value: number | null) => setBomOverride(key, value)

  /* Wie viele Positionen das PLM ueberhaupt belegen KOENNTE — Achsen und feste
     Bauteile. Ohne diese Bezugsgroesse liest sich „0 gemessen" wie ein Ausfall
     statt wie ein Stand. */
  const belegbar = useMemo(
    () =>
      result.axes.filter((a) => a.mass > 0).reduce((n, a) => n + a.count, 0) +
      result.parts.reduce((n, p) => n + p.count, 0),
    [result.axes, result.parts],
  )

  /*
    DER FILTER IST BEDIENZUSTAND — mit einer Ausnahme, die alles entscheidet:
    an seinem Ende steht eine SUMME. Eine gefilterte Liste hat eine andere als
    die ganze, und wer die beiden verwechselt, trägt eine Teilmasse als
    Gesamtmasse weiter. Deshalb liefert `applyBomFilter` beides, und die
    Kopfzeile über der Tabelle sagt es an.
  */
  const [filter, setFilter] = useState<BomFilter>(EMPTY_FILTER)
  const gefiltert = useMemo(
    () => applyBomFilter(result.axes, result.parts, filter),
    [result.axes, result.parts, filter],
  )
  const alleGruppen = useMemo(
    () => bomGroups(result.axes, result.parts),
    [result.axes, result.parts],
  )
  const aktiv = filterActive(filter)

  const groups = useMemo(() => {
    const order: string[] = []
    const byGroup = new Map<string, typeof result.axes>()
    for (const axis of gefiltert.axes) {
      if (!byGroup.has(axis.group)) {
        byGroup.set(axis.group, [])
        order.push(axis.group)
      }
      byGroup.get(axis.group)!.push(axis)
    }
    return order.map((name) => ({ name, axes: byGroup.get(name)! }))
  }, [gefiltert.axes])

  const partGroups = useMemo(() => {
    const order: string[] = []
    const byGroup = new Map<string, typeof result.parts>()
    for (const part of gefiltert.parts) {
      if (!byGroup.has(part.group)) {
        byGroup.set(part.group, [])
        order.push(part.group)
      }
      byGroup.get(part.group)!.push(part)
    }
    return order.map((name) => ({ name, parts: byGroup.get(name)! }))
  }, [gefiltert.parts])

  const diverges = !Number.isFinite(result.fixedPoint)

  return (
    <div className="stack">
      <PageHead
        title="Stückliste"
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {/* Die Annahmen werden unter Parameter zurückgesetzt — hier stünde
                ein Knopf für Werte, die auf dieser Seite gar nicht mehr
                verstellt werden. */}
            {result.overrides > 0 && (
              <Button size="sm" variant="ghost" onClick={resetBomOverrides}>
                Schätzwerte wiederherstellen
              </Button>
            )}
          </div>
        }
      />

      <div className="metrics">
        <Metric
          label="Gesamtmasse"
          value={`${fmt(result.total, 1)} kg`}
          note={`aus ${result.drivenAxes} angetriebenen Achsen`}
        />
        <Metric
          label="Gegen die Eingabe"
          value={`${result.deviation >= 0 ? '+' : '−'}${fmt(Math.abs(result.deviation), 1)} kg`}
          note={`eingestellte Körpermasse ${fmt(state.body, 0)} kg`}
          warn={Math.abs(result.deviation) > 5}
        />
        <Metric
          label="Antriebe"
          value={`${fmt(result.actuatorMass, 1)} kg`}
          note={`Ziel ${DRIVE_MASS_TARGET} kg · davon ${fmt(SET_MOTOR_MASS, 1)} kg Motoren`}
          warn={result.actuatorMass > DRIVE_MASS_TARGET + 0.5}
        />
        <Metric
          label="Fixpunkt der Schleife"
          value={diverges ? '∞' : `${fmt(result.fixedPoint, 1)} kg`}
          note={
            diverges
              ? 'der Entwurf trägt sich nicht selbst'
              : `Verstärkung ×${fmt(result.amplification)}`
          }
          warn={diverges}
        />
        <Metric
          label="Kosten"
          value={`${eur(result.cost.total)} €`}
          note="Annahme — es gibt keine Preisliste"
        />
      </div>

      {diverges && (
        <Alert tone="danger" title="Die Massenschleife divergiert">
          Antriebe, Akku und Kondensator machen {Math.round(result.scalingShare * 100)} % der
          Körpermasse aus, die Struktur weitere {Math.round(params.structureShare * 100)} %.
          Zusammen bleibt kein Rest, aus dem die feste Ausstattung getragen werden könnte: jedes
          zusätzliche Newtonmeter kostet mehr Aktuatormasse, als es tragen kann. Mit dieser
          Momentendichte ist der Entwurf nicht baubar — mehr Nm/kg, weniger Sicherheitsfaktor oder
          ein leichterer Aufbau.
        </Alert>
      )}

      {/*
        DIE KOSTEN STEHEN IN EINER EIGENEN KARTE und nicht als Spalte neben den
        Massen. Der Grund ist nicht Platz, sondern Herkunft: die Massen folgen
        aus dem Modell und den Antriebsdaten, die Kosten aus einer Tabelle von
        Grössenordnungen, die niemand angeboten hat. Nebeneinander in derselben
        Zeile sähen beide gleich belastbar aus.
      */}
      <Card title="Kosten">
        <Alert tone="warning" title="Diese Zahlen sind Annahmen">
          Zu diesem Entwurf gibt es keine Preisliste, keine Angebote und keinen Einkauf. Die Sätze
          unten ordnen die Positionen zueinander — sie sagen, dass die Antriebe rund das Dreifache
          der Elektronik kosten, nicht was sie kosten. Wer echte Zahlen hat, ersetzt eine Tabelle im
          Kern (<code>COST_RATES</code>) und ändert sonst nichts.
        </Alert>
        <Table>
          <thead>
            <tr>
              <Th>Gruppe</Th>
              <Th align="right">Kosten</Th>
              <Th align="right">Anteil</Th>
              <Th>Woraus</Th>
            </tr>
          </thead>
          <tbody>
            {COST_ROWS.map((row) => {
              const value = result.cost[row.key]
              return (
                <tr key={row.key}>
                  <Td>{row.label}</Td>
                  <Td align="right" numeric>
                    {eur(value)} €
                  </Td>
                  <Td align="right" numeric>
                    <span className="cell-muted">
                      {result.cost.total > 0 ? Math.round((100 * value) / result.cost.total) : 0} %
                    </span>
                  </Td>
                  <Td>
                    <span className="cell-muted">{row.basis}</span>
                  </Td>
                </tr>
              )
            })}
            <tr className="feat__base">
              <Td>
                <b>Summe</b>
              </Td>
              <Td align="right" numeric>
                <b>{eur(result.cost.total)} €</b>
              </Td>
              <Td align="right" numeric>
                <span className="cell-muted">100 %</span>
              </Td>
              <Td>
                <span className="cell-muted">
                  {eur(result.cost.total / Math.max(1, result.total))} € je Kilogramm
                </span>
              </Td>
            </tr>
          </tbody>
        </Table>
      </Card>

      {/*
        DIE KARTE, DIE DIE FRAGE BEANTWORTET: wie belastbar ist diese Masse?

        Eine Gesamtmasse sagt WIE VIEL. Sie sagt nicht, wie viel davon jemand
        gewogen und wie viel jemand hochgerechnet hat — und daran haengt, ob
        eine Zahl in eine Freigabe darf. Solange das PLM keine Positionen
        belegt, ist die Antwort unbequem und richtig: nichts an dieser Maschine
        ist gewogen.
      */}
      <Card
        title="Massenherkunft"
        eyebrow={`${result.provenance.plm.parts} von ${belegbar} Positionen gemessen`}
      >
        {result.provenance.plm.parts === 0 && (
          <Alert tone="info" title="Das PLM belegt noch keine Position">
            Die Anbindung steht und liefert Segmentdaten — Masse, Schwerpunkt und Trägheit je
            Körperteil. Was fehlt, ist die Zuordnung eines Teils zu einer Position dieser
            Liste: das Feld <code>bomKey</code> im Vertrag. Ein Oberschenkel ist ein Segment
            und enthält mehrere Positionen; ohne diese Zuordnung lässt sich nicht sagen,
            welche Zeile gemessen ist. Bis dahin trägt jede Zeile ihre eigene Herkunft, und
            keine davon ist eine Messung.
          </Alert>
        )}
        <Table>
          <thead>
            <tr>
              <Th>Herkunft</Th>
              <Th align="right">Positionen</Th>
              <Th align="right">Masse</Th>
              <Th align="right">Anteil</Th>
              <Th>Was das heisst</Th>
            </tr>
          </thead>
          <tbody>
            {HERKUNFT_ORDER.map((key) => {
              const zeile = result.provenance[key]
              return (
                <tr key={key}>
                  <Td>
                    <Badge tone={HERKUNFT[key].tone}>{HERKUNFT[key].label}</Badge>
                  </Td>
                  <Td align="right" numeric>
                    {zeile.parts}
                  </Td>
                  <Td align="right" numeric>
                    {fmt(zeile.mass, 1)} kg
                  </Td>
                  <Td align="right" numeric>
                    {result.total > 0 ? `${Math.round((zeile.mass / result.total) * 100)} %` : '—'}
                  </Td>
                  <Td>
                    <span className="cell-muted">{HERKUNFT_NOTE[key]}</span>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
        <p className="cell-muted" style={{ marginTop: 'var(--space-3)' }}>
          Die Struktur ({fmt(result.structureMass, 1)} kg) steht nicht in dieser Rechnung. Sie
          ist kein Posten, sondern ein Anteil der Gesamtmasse — ihre Herkunft ist die Annahme
          darüber, nicht eine Position. Sie hier als „geschätzt" mitzuzählen würde die Zahl
          verwässern statt sie zu erklären.
        </p>
      </Card>

      {result.tooFastAxes > 0 && (
        <Alert tone="warning" title="Zu schnell für das Modul">
          {result.tooFastAxes} Achsen verlangen mehr Abtriebsdrehzahl als die eingestellten{' '}
          {fmt(params.moduleSpeed, 0)} °/s. Für sie braucht es eine kürzere Übersetzung — und damit
          einen anderen Punkt auf der Momentenkurve, als diese Liste unterstellt.
        </Alert>
      )}

      <Card
        title="Positionen"
        flush
        /* Die einzige Karte, die auf das Blatt kommt — siehe den Druckstil in
           `sim.css`. Wer eine Stückliste ausdruckt, will die Liste und nicht
           das Dossier drumherum. */
        data-print="keep"
        eyebrow={
          aktiv
            ? `${gefiltert.count} von ${gefiltert.countAll} Positionen · ${fmt(gefiltert.mass, 1)} von ${fmt(gefiltert.massAll, 1)} kg`
            : result.overrides > 0
              ? `${result.overrides} Position${result.overrides > 1 ? 'en' : ''} überschrieben — die bleiben stehen, auch wenn sich Gangart oder Regler ändern`
              : 'jede Einzelmasse ist überschreibbar — Wert eintragen und Feld verlassen; der Schätzwert steht im Tooltip'
        }
        actions={
          <div className="bomfilter__actions">
            {aktiv && (
              <Button size="sm" variant="ghost" onClick={() => setFilter(EMPTY_FILTER)}>
                Filter aufheben
              </Button>
            )}
            {/*
              DRUCKEN HEISST HIER: DIE AUSWAHL, DIE MAN SIEHT.

              `window.print()` und kein eigener Export — was auf dem Blatt
              landet, ist dieselbe Tabelle, die auf dem Bildschirm steht, nur
              ohne Bedienelemente. Ein zweiter Weg zum selben Papier wäre eine
              zweite Wahrheit, und die erste Abweichung fiele erst auf dem
              Ausdruck auf.
            */}
            <Button size="sm" variant="secondary" onClick={() => window.print()}>
              Drucken
            </Button>
          </div>
        }
      >
        {/*
          NUR IM AUSDRUCK. Ein Blatt mit einer Teilsumme, dem man nicht ansieht,
          WAS gefiltert war, ist eine Zahl ohne Herkunft. Der Kopf trägt sie
          mit — Auswahl, Deckung, Datum, Stand.
        */}
        <div className="bomprint">
          <h2 className="bomprint__title">Stückliste Gen5</h2>
          <dl className="bomprint__meta">
            <dt>Auswahl</dt>
            <dd>{filterSummary(filter, HERKUNFT_TEXT)}</dd>
            <dt>Umfang</dt>
            <dd>
              {gefiltert.count} von {gefiltert.countAll} Positionen ·{' '}
              {fmt(gefiltert.mass, 1)} von {fmt(gefiltert.massAll, 1)} kg
              {aktiv && ' — Teilsumme, nicht die Gesamtmasse'}
            </dd>
            <dt>Gesamtmasse</dt>
            <dd>
              {fmt(result.total, 1)} kg einschliesslich {fmt(result.structureMass, 1)} kg Struktur
            </dd>
            <dt>Gedruckt</dt>
            <dd>{new Date().toLocaleString('de-DE')}</dd>
          </dl>
          <p className="bomprint__warn">
            Keine dieser Massen ist gewogen. {result.provenance.plm.parts} von{' '}
            {gefiltert.countAll} Positionen sind aus dem PLM belegt; alles andere ist
            ausgewählt, von Hand gesetzt oder hochgerechnet. Kein Blatt für eine Freigabe.
          </p>
        </div>

        <div className="bomfilter">
          <label className="bomfilter__field">
            <span className="bomfilter__label">Suche</span>
            <input
              className="bomfilter__input"
              type="search"
              value={filter.text}
              placeholder="Position, Motor, Teilenummer"
              onChange={(e) => setFilter((f) => ({ ...f, text: e.target.value }))}
            />
          </label>

          <div className="bomfilter__field">
            <span className="bomfilter__label">Gruppe</span>
            <div className="bomfilter__chips">
              {alleGruppen.map((g) => (
                <button
                  key={g}
                  type="button"
                  className={`chip${filter.groups.includes(g) ? ' chip--on' : ''}`}
                  aria-pressed={filter.groups.includes(g)}
                  onClick={() =>
                    setFilter((f) => ({
                      ...f,
                      groups: f.groups.includes(g)
                        ? f.groups.filter((x) => x !== g)
                        : [...f.groups, g],
                    }))
                  }
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="bomfilter__field">
            <span className="bomfilter__label">Herkunft</span>
            <div className="bomfilter__chips">
              {HERKUNFT_ORDER.map((key) => {
                const an = filter.sources.includes(key)
                const zahl = result.provenance[key].parts
                return (
                  <button
                    key={key}
                    type="button"
                    className={`chip${an ? ' chip--on' : ''}`}
                    aria-pressed={an}
                    /* Eine Herkunft ohne Positionen bleibt sichtbar, aber
                       abgeblendet: dass es sie gibt und sie leer ist, ist die
                       Auskunft — heute bei „gemessen". */
                    disabled={zahl === 0}
                    onClick={() =>
                      setFilter((f) => ({
                        ...f,
                        sources: an
                          ? f.sources.filter((x) => x !== key)
                          : [...f.sources, key],
                      }))
                    }
                  >
                    {HERKUNFT[key].label}
                    <span className="chip__count">{zahl}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <label className="bomfilter__field bomfilter__field--slim">
            <span className="bomfilter__label">ab je Stück</span>
            <select
              className="bomfilter__input"
              value={filter.minMass}
              onChange={(e) => setFilter((f) => ({ ...f, minMass: Number(e.target.value) }))}
            >
              <option value={0}>alle</option>
              <option value={0.1}>0,1 kg</option>
              <option value={0.5}>0,5 kg</option>
              <option value={1}>1 kg</option>
              <option value={2}>2 kg</option>
            </select>
          </label>
        </div>

        <Table>
          <thead>
            <tr>
              <Th>Position</Th>
              <Th align="right">Anzahl</Th>
              <Th align="right">Moment</Th>
              <Th align="right">Drehzahl</Th>
              <Th align="right">Auslegung</Th>
              <Th align="right">je Stück</Th>
              <Th align="right">Summe</Th>
              <Th>Herkunft</Th>
              <Th>Ausführung</Th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.name}>
                <tr>
                  <Td colSpan={9}>
                    <span className="eyebrow">{group.name}</span>
                  </Td>
                </tr>
                {group.axes.map((axis) => (
                  <tr key={axis.key}>
                    <Td>
                      {axis.label}
                      {axis.note && <span className="cell-muted"> — {axis.note}</span>}
                    </Td>
                    <Td align="right" numeric>
                      {axis.count}
                    </Td>
                    <Td align="right" numeric>
                      {axis.moment > 0 ? `${num(axis.moment, 0)} Nm` : '—'}
                    </Td>
                    <Td align="right" numeric>
                      {axis.speed > 0 ? (
                        <span className={axis.tooFast ? 'cell-warn' : ''}>
                          {num(axis.speed, 0)} °/s
                        </span>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td align="right" numeric>
                      {axis.moment > 0 ? `${num(axis.required, 0)} Nm` : '—'}
                    </Td>
                    <Td align="right" numeric>
                      {axis.estimate > 0 ? (
                        <MassCell
                          value={axis.mass}
                          estimate={axis.estimate}
                          overridden={axis.overridden}
                          onChange={setOverride(axis.key)}
                        />
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td align="right" numeric>
                      {fmt(axis.mass * axis.count)} kg
                    </Td>
                    <Td>
                      <Herkunft row={axis} />
                    </Td>
                    <Td>
                      {/*
                        DIE AUSFÜHRUNG — was verbaut ist, nicht woher die Zahl kommt.

                        Vorher stand hier, woher das MOMENT kommt — inverse
                        Dynamik oder statische Schätzung. Das war die
                        interessantere Frage, solange jede Masse gleich
                        entstand. Seit die Motorisierung des Stands
                        gesetzt ist, entstehen sie unterschiedlich, und eine
                        Summe, in der eine Auswahl und eine Faustformel
                        ununterscheidbar addiert werden, trägt keine
                        Entscheidung.

                        Die Momentherkunft steht weiter darunter — sie ist
                        nicht weg, nur nicht mehr die erste Zeile.
                      */}
                      {axis.setup ? (
                        <>
                          <span className="cell-set">
                            {MOTOR_BY_ID.get(axis.setup.motorId)?.label ?? axis.setup.motorId}
                          </span>
                          <span className="cell-muted">
                            {' '}
                            {axis.setup.motorsPerAxis > 1 ? `${axis.setup.motorsPerAxis}× · ` : ''}
                            {num(axis.setup.ratio, axis.setup.ratio % 1 ? 3 : 0)}:1
                          </span>
                        </>
                      ) : (
                        <span className="cell-muted">geschätzt</span>
                      )}
                      <br />
                      <span className="cell-muted">{axis.source}</span>
                    </Td>
                  </tr>
                ))}
              </Fragment>
            ))}

            {/*
              DIE SUMMENZEILEN GELTEN FÜR DIE GANZE MASCHINE — und verschwinden
              deshalb, sobald gefiltert wird. Eine Zeile „Antriebe gesamt: 24,0
              kg" unter einer Liste, die nur die Beine zeigt, ist keine
              Zwischensumme, sondern eine falsche Fährte.

              Was stattdessen kommt, steht ganz unten: die Summe der AUSWAHL,
              und daneben, wovon sie ein Teil ist.
            */}
            {!aktiv && (
            <tr className="bom__sum">
              <Td>
                <b>Antriebe gesamt</b>
              </Td>
              <Td align="right" numeric>
                {result.drivenAxes}
              </Td>
              <Td colSpan={5} />
              <Td align="right" numeric>
                <b>{fmt(result.actuatorMass, 1)} kg</b>
              </Td>
              <Td />
            </tr>
            )}

            {!aktiv && (
            <tr>
              <Td colSpan={9}>
                <span className="eyebrow">Energie</span>
              </Td>
            </tr>
            )}
            {!aktiv && (
              <>
            <tr>
              <Td>Akkupack</Td>
              <Td align="right" numeric>
                1
              </Td>
              <Td colSpan={2} />
              <Td align="right" numeric>
                {num(result.batteryEnergy, 0)} Wh
              </Td>
              <Td align="right" numeric>
                <MassCell
                  value={result.batteryMass}
                  estimate={result.batteryEstimate}
                  overridden={result.batteryOverridden}
                  onChange={setOverride('bat')}
                />
              </Td>
              <Td align="right" numeric>
                {fmt(result.batteryMass)} kg
              </Td>
              <Td>
                {/* Akku und Kondensator sind keine Stuecklistenpositionen im
                    Sinne von `parts` — ihre Masse folgt aus Inhalt und
                    Energiedichte. Herkunft haben sie trotzdem, sonst faellt in
                    der Spalte eine Luecke auf, die keine Aussage ist. */}
                <Badge tone={result.batteryOverridden ? 'accent' : 'warning'}>
                  {result.batteryOverridden ? 'von Hand' : 'geschätzt'}
                </Badge>
              </Td>
              <Td>
                <span className="cell-muted">
                  {result.batteryOverridden
                    ? 'gesetzt'
                    : `${num(result.averagePower, 0)} W mittlere Antriebsleistung × ${fmt(params.runtime, 1)} h`}
                </span>
              </Td>
            </tr>
            <tr>
              <Td>Pufferkondensator am Zwischenkreis</Td>
              <Td align="right" numeric>
                1
              </Td>
              <Td colSpan={2} />
              <Td align="right" numeric>
                {num(result.capacitance * 1000, 0)} mF
              </Td>
              <Td align="right" numeric>
                <MassCell
                  value={result.capacitorMass}
                  estimate={result.capacitorEstimate}
                  overridden={result.capacitorOverridden}
                  onChange={setOverride('cap')}
                />
              </Td>
              <Td align="right" numeric>
                {fmt(result.capacitorMass)} kg
              </Td>
              <Td>
                {/* Akku und Kondensator sind keine Stuecklistenpositionen im
                    Sinne von `parts` — ihre Masse folgt aus Inhalt und
                    Energiedichte. Herkunft haben sie trotzdem, sonst faellt in
                    der Spalte eine Luecke auf, die keine Aussage ist. */}
                <Badge tone={result.capacitorOverridden ? 'accent' : 'warning'}>
                  {result.capacitorOverridden ? 'von Hand' : 'geschätzt'}
                </Badge>
              </Td>
              <Td>
                <span className="cell-muted">
                  {result.capacitorOverridden
                    ? 'gesetzt'
                    : `${num(result.burstEnergy, 0)} J Spitzenüberschuss bei ${fmt(params.busVoltage, 0)} V, Einbruch bis ${fmt(result.minVoltage, 0)} V`}
                </span>
              </Td>
            </tr>
              </>
            )}

            {partGroups.map((group) => (
              <Fragment key={group.name}>
                <tr>
                  <Td colSpan={9}>
                    <span className="eyebrow">{group.name}</span>
                  </Td>
                </tr>
                {group.parts.map((part) => (
                  <tr key={part.key}>
                    <Td>{part.label}</Td>
                    <Td align="right" numeric>
                      {part.count}
                    </Td>
                    <Td colSpan={3} />
                    <Td align="right" numeric>
                      <MassCell
                        value={part.mass}
                        estimate={part.estimate}
                        overridden={part.overridden}
                        onChange={setOverride(part.key)}
                      />
                    </Td>
                    <Td align="right" numeric>
                      {fmt(part.count * part.mass)} kg
                    </Td>
                    <Td>
                      <Herkunft row={part} />
                    </Td>
                    <Td>
                      <span className="cell-muted">{part.note}</span>
                    </Td>
                  </tr>
                ))}
              </Fragment>
            ))}

            {!aktiv && (
              <>
            <tr>
              <Td colSpan={9}>
                <span className="eyebrow">Struktur</span>
              </Td>
            </tr>
            <tr>
              <Td>Rahmen, Gehäuse, Verkleidung, Verbindungselemente</Td>
              <Td align="right" numeric>
                1
              </Td>
              <Td colSpan={2} />
              <Td align="right" numeric>
                {Math.round(params.structureShare * 100)} %
              </Td>
              <Td />
              <Td align="right" numeric>
                {fmt(result.structureMass, 1)} kg
              </Td>
              <Td>
                {/* Die Struktur ist kein Posten, sondern ein Anteil. Ihre
                    Herkunft ist die Annahme `structureShare` — deshalb steht
                    sie auch nicht in der Deckungsrechnung. */}
                <Badge tone="warning">Annahme</Badge>
              </Td>
              <Td>
                <span className="cell-muted">Anteil an der Gesamtmasse</span>
              </Td>
            </tr>
              </>
            )}

            {aktiv ? (
              /*
                GEFILTERT: DIE SUMME DER AUSWAHL, und daneben, wovon sie ein
                Teil ist. Beide Zahlen in einer Zeile, weil sie nur zusammen
                eine Aussage sind — die Teilsumme allein ist die Zahl, die man
                später für die Gesamtmasse hält.
              */
              <tr className="bom__sum">
                <Td colSpan={6}>
                  <b>Summe der Auswahl</b>
                  <span className="cell-muted"> · {filterSummary(filter, HERKUNFT_TEXT)}</span>
                </Td>
                <Td align="right" numeric>
                  {gefiltert.count}
                </Td>
                <Td align="right" numeric>
                  <b>{fmt(gefiltert.mass, 1)} kg</b>
                </Td>
                <Td>
                  <span className="cell-muted">
                    von {fmt(result.total, 1)} kg gesamt
                  </span>
                </Td>
              </tr>
            ) : (
              <tr className="bom__sum">
                <Td colSpan={7}>
                  <b>Gesamtmasse aus der Stückliste</b>
                </Td>
                <Td align="right" numeric>
                  <b>{fmt(result.total, 1)} kg</b>
                </Td>
                <Td>
                  <Badge tone={Math.abs(result.deviation) > 5 ? 'warning' : 'neutral'}>
                    {result.deviation >= 0 ? '+' : '−'}
                    {fmt(Math.abs(result.deviation), 1)} kg gegen die Eingabe
                  </Badge>
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      <Card title="Lesehinweise">
        <div className="balance__notes">
          <div>
            <span className="eyebrow">Die Rückkopplung</span>
            <p className="page-lead">
              Antriebe, Akku und Kondensator wachsen mit der Masse, Elektronik und Sensorik nicht.
              Aus <b>m′ = (a·m + c)/(1 − k)</b> folgt der Fixpunkt <b>m* = c/((1 − k) − a)</b>.
              Hier: a = {fmt(result.scalingShare, 3)}, c = {fmt(result.fixedMass, 1)} kg, k ={' '}
              {fmt(params.structureShare, 2)}. Wird der Nenner klein, kann die Maschine ihre eigenen
              Antriebe nicht mehr tragen.
            </p>
          </div>
          <div>
            <span className="eyebrow">Woher die Momente kommen</span>
            <p className="page-lead">
              Beine und Arme aus der inversen Dynamik — dieselben Zahlen wie in der Antriebsliste.
              Rumpf und Hals sind statisch geschätzt, weil es für sie keine Winkelbahn gibt, aus der
              sich ein Moment ableiten liesse; das steht in der Herkunftsspalte. Ein
              Zehengrundgelenk gibt es beim Gen5 nicht: der Fuss ist starr, und seine
              Abwesenheit in der Liste ist eine Entscheidung.
            </p>
          </div>
          <div>
            <span className="eyebrow">Warum ein Rückspeisegrad nötig ist</span>
            <p className="page-lead">
              Die inverse Dynamik ist verlustfrei: kein Getriebe, keine Reibung, kein
              Wicklungswiderstand. Über einen geschlossenen Gehzyklus ist die mechanische
              Nettoarbeit deshalb rechnerisch <b>negativ</b> — das Modell gewinnt beim Bremsen mehr
              zurück, als es beim Treiben ausgibt, und der Akku käme auf null Wattstunden. Mit einem
              realistischen Rückspeisegrad wird daraus wieder eine Zahl, die man einkaufen kann. Bei
              1,00 steht dort die untere Schranke des Verbrauchs, nicht die Auslegung.
            </p>
          </div>
          <div>
            <span className="eyebrow">Der Kondensator</span>
            <p className="page-lead">
              Gepuffert wird nur, was über der mittleren Leistung liegt — die Fläche zwischen
              Verlauf und Mittelwert, hier {num(result.burstEnergy, 0)} J über{' '}
              {fmt(result.burstTime, 2)} s. Die Kapazität folgt aus <b>C = 2E/(U² − U²min)</b> und
              nicht aus ½CU²: ein Kondensator gibt nur die Energie zwischen voller Spannung und der
              niedrigsten noch zulässigen ab. Spitzenstrom hier {num(result.peakCurrent, 0)} A bei{' '}
              {fmt(params.busVoltage, 0)} V.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
